import React from 'react';
import { Space, Tag, Tooltip, Typography } from 'antd';
import { ExclamationCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';

const { Text } = Typography;

const formatStationPair = (iata, icao) => {
    const cleanIata = (iata || '').trim().toUpperCase();
    const cleanIcao = (icao || '').trim().toUpperCase();
    if (cleanIata && cleanIcao) return `${cleanIata} (${cleanIcao})`;
    if (cleanIata) return cleanIata;
    if (cleanIcao) return cleanIcao;
    return '未识别';
};

const hasAnyStation = (iata, icao) => {
    return Boolean((iata || '').trim() || (icao || '').trim());
};

/**
 * 计算当前已加载列表每一行的断链提示条与隐藏前一段信息（T10①②、T39、D12、D30、D47、AC-S8-04）
 *
 * 规则：
 * 1. 仅当 view === 'reg' 且 !chainIsComputing 时生成行上方提示条（D15、AC-S8-04）。
 * 2. 仅已定位主行（chain_leg_no != null 且非占位、非疑似重复非主行、非无法定位）参与前一段衔接提示。
 * 3. 断链分类仅限两类：'conflict'（冲突）与 'recognition_issue'（识别问题），绝不出现「数据缺口」（D30）。
 * 4. 隐藏前一段提示（D47）：
 *    - 当前行是列表第一条已显示主行且 chain_prev_id 非空 -> 「前一段不在当前范围」
 *    - 上一条已显示主行存在但 id !== chain_prev_id -> 「中间有 N 条未显示」（N = 当前 chain_leg_no - 上一显示主行 chain_leg_no - 1）
 */
export const buildChainRowMetaMap = (data = [], { view = 'all', chainIsComputing = false } = {}) => {
    const map = new Map();
    if (!Array.isArray(data) || data.length === 0) {
        return map;
    }

    const enableChainBanners = view === 'reg' && !chainIsComputing;
    let prevDisplayedMain = null;

    data.forEach((row) => {
        if (!row || !row.id) return;

        const isPositionedMain =
            !row.isPlaceholderRow
            && row.chain_leg_no != null
            && !row.chain_dup_of_id
            && !row.chain_unplaced
            && row.status !== 'rejected'
            && row.status !== 'pending'
            && row.status !== 'recognizing';

        if (!enableChainBanners || !isPositionedMain) {
            map.set(String(row.id), { showBanner: false });
            return;
        }

        let hiddenNote = null;
        if (!prevDisplayedMain && row.chain_prev_id != null) {
            hiddenNote = '前一段不在当前范围';
        } else if (
            prevDisplayedMain
            && row.chain_prev_id != null
            && String(row.chain_prev_id) !== String(prevDisplayedMain.id)
        ) {
            const diff = Number(row.chain_leg_no) - Number(prevDisplayedMain.chain_leg_no) - 1;
            hiddenNote = diff > 0 ? `中间有 ${diff} 条未显示` : '前一段未显示';
        }

        const linkStatus = row.chain_link_status || 'none';
        const prevSummary = row.chain_prev_summary || prevDisplayedMain || null;
        const prevStation = formatStationPair(prevSummary?.to_iata, prevSummary?.to_icao);
        const currStation = formatStationPair(row.from_iata, row.from_icao);
        const prevTl = prevSummary?.flight_log_no ? `（上段 ${prevSummary.flight_log_no}）` : '';

        // D12: 若上一段到达站或本段出发站含 unconfirmed_fields 或 _reconciliation 推算标记，附不确定性说明
        const hasStationUncertainty = Boolean(
            prevSummary?.to_station_uncertain || row.from_station_uncertain
        );
        const uncertaintySuffix = hasStationUncertainty
            ? '（含未确认或推算站点，可能是识别或推算问题）'
            : '';

        if (linkStatus === 'conflict') {
            map.set(String(row.id), {
                showBanner: true,
                bannerType: 'conflict',
                bannerLabel: '冲突',
                bannerText: `上段到达 ${prevStation}${prevTl} ≠ 本段出发 ${currStation}${uncertaintySuffix}`,
                hiddenNote,
            });
        } else if (linkStatus === 'recognition_issue') {
            const prevMissing = !hasAnyStation(prevSummary?.to_iata, prevSummary?.to_icao);
            const currMissing = !hasAnyStation(row.from_iata, row.from_icao);
            let issueDetail = '出发站或到达站未识别，无法比较';
            if (prevMissing && currMissing) {
                issueDetail = `上段到达站${prevTl}与本段出发站均未识别，无法比较`;
            } else if (prevMissing) {
                issueDetail = `上段到达站${prevTl}未识别（本段出发 ${currStation}），无法比较`;
            } else if (currMissing) {
                issueDetail = `本段出发站未识别（上段到达 ${prevStation}${prevTl}），无法比较`;
            }
            map.set(String(row.id), {
                showBanner: true,
                bannerType: 'recognition_issue',
                bannerLabel: '识别问题',
                bannerText: `${issueDetail}${uncertaintySuffix}`,
                hiddenNote,
            });
        } else if (hiddenNote) {
            map.set(String(row.id), {
                showBanner: true,
                bannerType: 'hidden_only',
                bannerLabel: null,
                bannerText: '已与真实前一段衔接',
                hiddenNote,
            });
        } else {
            map.set(String(row.id), { showBanner: false });
        }

        prevDisplayedMain = row;
    });

    return map;
};

/**
 * 行内状态列附加航段链诊断徽标
 * 仅在单机号二级页签（view === 'reg'，核对航段链视图）展示，避免在默认「全部」页签堆叠视觉噪音：
 * - 疑似重复
 * - 无法定位
 * - 时间顺序异常
 * - 时间可能识别错误
 */
export const FlightLogChainStatusBadges = ({ record, view = 'all' }) => {
    if (!record) return null;

    if (record.isPlaceholderRow) {
        return (
            <Tag color="default" style={{ margin: 0 }}>
                已失效占位
            </Tag>
        );
    }

    const isRegView = view === 'reg';
    if (!isRegView) return null;

    const isSuspectedDup = Boolean(record.chain_dup_of_id || record.is_suspected_duplicate);
    const isUnplaced = Boolean(record.chain_unplaced);
    const hasTimeOrderWarning = Boolean(record.chain_time_order_warning);
    const hasBlockHoursWarning = Boolean(record.block_hours_warning);

    if (!isSuspectedDup && !isUnplaced && !hasTimeOrderWarning && !hasBlockHoursWarning) {
        return null;
    }

    const prevArrivalText =
        record.chain_prev_summary?.inferred_arrival_utc
        || record.chain_prev_summary?.on_chocks_utc
        || '未知';
    const currDepartureText =
        record.inferred_departure_utc
        || record.off_chocks_utc
        || '未知';

    return (
        <Space direction="vertical" size={2}>
            {isSuspectedDup && (
                <Tooltip title="同机号 + 同 TL No. 非主行，不参与断链判定">
                    <Tag color="purple" style={{ margin: 0 }}>
                        疑似重复
                    </Tag>
                </Tooltip>
            )}
            {isUnplaced && (
                <Tooltip title="飞行日期为空，无法在航段链中定位，排在已定位航段之后">
                    <Tag color="default" style={{ margin: 0 }}>
                        无法定位
                    </Tag>
                </Tooltip>
            )}
            {hasTimeOrderWarning && (
                <Tooltip title={`本段撤轮挡时刻（${currDepartureText}）早于上一段到达时刻（${prevArrivalText}）`}>
                    <Tag color="warning" icon={<WarningOutlined />} style={{ margin: 0 }}>
                        时间顺序异常
                    </Tag>
                </Tooltip>
            )}
            {hasBlockHoursWarning && (
                <Tooltip
                    title={`识别 Block Hours（${record.block_hours || '-'}）与轮挡起止推算时长（${record.inferred_block_hours || '-'}）相差超过 1 小时`}
                >
                    <Tag color="orange" icon={<WarningOutlined />} style={{ margin: 0 }}>
                        时间可能识别错误
                    </Tag>
                </Tooltip>
            )}
        </Space>
    );
};

/**
 * 构造 Ant Design Table 自定义 body.row 组件：
 * 在需要展示断链提示或隐藏前一段说明的已定位主行上方插入提示横幅行（T10①②、T39）
 */
export const createFlightLogBodyRow = (rowMetaMap, dataByIdMap) => {
    const CustomBodyRow = (props) => {
        const rowKey = props['data-row-key'];
        const meta = rowKey != null ? rowMetaMap.get(String(rowKey)) : null;
        const record = rowKey != null ? dataByIdMap.get(String(rowKey)) : null;

        const mergedStyle = { ...(props.style || {}) };
        if (record?.isPlaceholderRow) {
            mergedStyle.background = '#f5f5f5';
            mergedStyle.color = '#8c8c8c';
            mergedStyle.opacity = 0.72;
            mergedStyle.cursor = 'default';
        } else if (record?.chain_dup_of_id || record?.is_suspected_duplicate) {
            mergedStyle.background = '#fafafa';
            mergedStyle.color = '#595959';
        } else if (record?.status === 'rejected') {
            mergedStyle.color = '#8c8c8c';
        } else if (record?.id) {
            mergedStyle.cursor = 'pointer';
        }

        if (!meta?.showBanner) {
            return <tr {...props} style={mergedStyle} />;
        }

        const isConflict = meta.bannerType === 'conflict';
        const isIssue = meta.bannerType === 'recognition_issue';

        const bannerBg = isConflict ? '#fff2f0' : '#fafafa';
        const bannerBorder = isConflict ? '#ffccc7' : '#e8e8e8';
        const bannerColor = isConflict ? '#cf1322' : '#595959';

        return (
            <>
                <tr
                    className={`flight-log-chain-banner-row flight-log-chain-banner-${meta.bannerType}`}
                    style={{ background: bannerBg }}
                >
                    <td
                        colSpan={100}
                        style={{
                            padding: '4px 12px',
                            background: bannerBg,
                            borderTop: `1px solid ${bannerBorder}`,
                            borderBottom: `1px dashed ${bannerBorder}`,
                            color: bannerColor,
                            fontSize: 12,
                        }}
                    >
                        <Space size={8} wrap>
                            {isConflict && (
                                <Tag color="error" icon={<ExclamationCircleOutlined />} style={{ margin: 0 }}>
                                    {meta.bannerLabel}
                                </Tag>
                            )}
                            {isIssue && (
                                <Tag color="default" icon={<InfoCircleOutlined />} style={{ margin: 0 }}>
                                    {meta.bannerLabel}
                                </Tag>
                            )}
                            <Text
                                strong={isConflict}
                                style={{
                                    fontSize: 12,
                                    color: bannerColor,
                                }}
                            >
                                {meta.bannerText}
                            </Text>
                            {meta.hiddenNote && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {`（${meta.hiddenNote}）`}
                                </Text>
                            )}
                        </Space>
                    </td>
                </tr>
                <tr {...props} style={mergedStyle} />
            </>
        );
    };

    return CustomBodyRow;
};
